import { forwardRef, useState, memo, useMemo, useCallback } from 'react';

import groupBy from 'lodash/groupBy';
import reduce from 'lodash/reduce';
import sum from 'lodash/sum';
import * as Recharts from 'recharts';
import { CHANGE_RATE, YAxisPercent } from '../Axis/index';
import { Brush } from '../Brush/index';
import { Legend, LegendContent } from '../Legend/index';
import {
  useAnnotationsElementMemo,
  useDateAxisElementsMemo,
  useMetricsElementsMemo,
  useTooltipElementsMemo,
  useWeekends,
  useYAxisElementsMemo,
} from '../hooks/index';
import { HIDDEN_Y_AXIS_ID } from './constants';
import { useTheme } from '@gogame/ui/styles';

const _referenceAreaRenderRect = Recharts.ReferenceArea.renderRect;

Recharts.ReferenceArea.renderRect = (option, { x: xProp, ...props } = {}) => {
  let x = xProp;

  if (props.xAxis.realScaleType === 'band') {
    x += props.width / 2;
  }

  return _referenceAreaRenderRect(option, {
    ...props,
    x,
  });
};

const ComposedChart = forwardRef(
  (
    {
      showWeekends,
      getCategoryPeriod,
      data,
      metrics,
      percentage,
      colorGetter,
      dateAxis,
      compareMode,
      metricsTooltip,
      annotationsMetric,

      onAnnotationHover,
      onAnnotationSelect,

      unknown,
      chartState,
      label,
      legend,
      brush,
      onChangeBrushPosition,
      onToggleMetric,
      onChangeAxis,
      id,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const hiddenMetrics = chartState.hiddenMetrics;
    const ownAxisMetrics = chartState.ownAxisMetrics;
    const [hoveredMetric, setHoveredMetricId] = useState(null);
    // scale: 'point', // for remove padding inside chart https://github.com/recharts/recharts/issues/367
    // Bar chart doesn't calc size automatically

    const xAxisScale = useMemo(() => {
      return metrics.some(({ type }) => type === 'bar') ? undefined : 'point';
    }, [metrics]);

    const hasChangeRage = useMemo(() => {
      return metrics.some(({ yAxisGroup }) => yAxisGroup === CHANGE_RATE);
    }, [metrics]);

    // show weekends only for daily scale
    const weekends = useWeekends(
      showWeekends ? data : [],
      getCategoryPeriod(0)[0]
    );

    const yAxisGroups = useMemo(() => {
      return groupBy(
        metrics.filter(
          ({ yAxisGroup }) =>
            yAxisGroup !== undefined && yAxisGroup !== CHANGE_RATE
        ),
        'yAxisGroup'
      );
    }, [metrics, ownAxisMetrics]);

    const yAxisGroupIds = useMemo(() => {
      return [
        ...Object.keys(yAxisGroups).map((id) => parseInt(id)),
        ...ownAxisMetrics,
      ];
    }, [yAxisGroups, ownAxisMetrics]);

    const chartData = useMemo(() => {
      if (!percentage) {
        return data;
      }

      return data.map((row) => {
        const values = reduce(
          Object.keys(yAxisGroups),
          (acc, groupKey) => {
            const metricKeys = yAxisGroups[groupKey].map(
              ({ dataKey }) => dataKey
            );
            const total = sum(metricKeys.map((metricId) => row[metricId]));

            return {
              ...acc,
              ...metricKeys.reduce(
                (result, dataKey) => ({
                  ...result,
                  [dataKey]: Math.floor(([row[dataKey]] / total) * 10000) / 100,
                }),
                {}
              ),
            };
          },
          {}
        );
        return { ...row, ...values };
      });
    }, [percentage, data, yAxisGroupIds]);

    const yAxisElements = useYAxisElementsMemo({ yAxisGroupIds });

    const metricElements = useMetricsElementsMemo({
      metrics,
      colorGetter,
      data: percentage ? chartData : data,
      hiddenMetrics: hiddenMetrics,
      hoveredMetric: hoveredMetric,
      ownAxisMetrics: ownAxisMetrics,
      percentage,
    });

    const tooltipElement = useTooltipElementsMemo({
      metricsTooltip,
      data,
      percentage,
      compareMode,
      getCategoryPeriod,
    });

    const dateAxisElements = useDateAxisElementsMemo({
      dateAxis,
      getCategoryPeriod,
      compareMode,
      xAxisScale,
    });

    const annotationsMetricElement = useAnnotationsElementMemo({
      chartData,
      onAnnotationHover: onAnnotationHover,
      onAnnotationSelect: onAnnotationSelect,
      highlightingStart: chartState.highlighting.start,
      xAxisScale,
      ...annotationsMetric,
      ...chartState.annotations,
    });

    const empty = useMemo(() => chartData.length === 0, [chartData.length]);
    const _metricIds = useMemo(() => metrics.map(({ id }) => id), [metrics]);

    const sortByMetricId = useCallback(
      ({ payload }) => {
        return _metricIds.indexOf(payload.id);
      },
      [_metricIds]
    );

    return (
      <Recharts.ComposedChart {...props} ref={ref} data={chartData}>
        {weekends.map(([x1, x2]) => {
          return (
            <Recharts.ReferenceArea
              x1={x1}
              x2={x2}
              fill={theme.palette.pending.main}
              fillOpacity={theme.palette.action.focusOpacity}
              key={x1}
            />
          );
        })}
        {metricElements}
        {annotationsMetricElement}
        {hasChangeRage ? (
          <YAxisPercent width={30} yAxisId={CHANGE_RATE} />
        ) : null}
        {yAxisElements}
        {dateAxisElements}
        {tooltipElement}
        {unknown}

        {/*don't use next line?*/}
        <Recharts.ReferenceArea
          x1={chartState.highlighting.start}
          x2={chartState.highlighting.end}
          fill={'#FF0000'}
          fillOpacity={0.3}
        />
        {!empty && legend ? (
          <Legend
            content={
              <LegendContent
                onToggle={onToggleMetric}
                onChangeAxis={onChangeAxis}
                onHover={setHoveredMetricId}
                hiddenMetrics={hiddenMetrics}
                ownAxisMetrics={ownAxisMetrics}
                sort={sortByMetricId}
                withMoveToOwnAxis={true}
              />
            }
          />
        ) : null}
        {!empty && brush ? (
          <Brush
            onChange={onChangeBrushPosition}
            {...chartState.brush}
            stroke={theme.palette.text.disabled}
            fill={theme.palette.background.paper}
          />
        ) : null}
        <Recharts.CartesianGrid
          vertical={false}
          strokeDasharray={'3 3'}
          stroke={theme.palette.inactive.main}
        />
        <Recharts.YAxis hide={true} width={0} height={0} />
        <Recharts.YAxis
          hide={true}
          width={0}
          height={0}
          yAxisId={HIDDEN_Y_AXIS_ID}
        />
        <use xlinkHref={'#active-annotation'} />
      </Recharts.ComposedChart>
    );
  }
);

const propsArray = [
  'height',
  'width',
  'getCategoryPeriod',
  'showWeekends',
  'metrics',
  'colorGetter',
  'dateAxis',
  'label',
  'compareMode',
  'metricsTooltip',
  'annotationsMetric',
  'data',
  'margin',
  'percentage',
  'unknown',
  'legend',
  'brush',
  'chartState',
  'onAnnotationHover',
  'onAnnotationSelect',
  'onChangeBrushPosition',
  'onChangeBrushPosition',
  'onToggleMetric',
];

function areEqual(prevProps, nextProps) {
  return propsArray.every((prop) => {
    const equal = prevProps[prop] === nextProps[prop];
    return equal;
  });
}

export default memo(ComposedChart, areEqual);
